import { ControllerInterface } from "csi/action/Controller";
import { ILocaleProvider } from "designSystem/locale";
import { ControllerSaveFunction, convertNodesToComponentItems } from "csi/middleware/common";
import { C8Node, ICache, cnl } from "csi/typings";
import { CreateSharedDataSheetsWizardStore, SourceTable } from "./CreateSharedDataSheetsWizardStore";
import Sourcing from "../../main.js";
import { ComponentItem } from "designSystem/types/component-item-types";
import xml from "csi/xml";
import JsonResponse from "csi/comm/JsonResponse";
import Writer from "csi/xml/Writer";
import { submitWriter } from "csi/service/writerService";
import PostRequest from "csi/comm/PostRequest";
import FormData from "csi/action/FormData";

/** Create and initialize CreateSharedDataSheetsStore */
export const createSharedDataSheetsStore = (
	ac: ControllerInterface,
	onClose: () => void,
	onSave: ControllerSaveFunction,
	locale: ILocaleProvider,
): CreateSharedDataSheetsWizardStore => {
	const cache = ac.getCache();
	const formData = ac.getFormData();
	const actionSourceNode = ac.getParent();
	const actionSourceNodeUrl = actionSourceNode.$URL;
	const sources = getSources(actionSourceNode, cache);
	const sourceOptions = getSourceOptions(actionSourceNode, sources, locale);
	const products = getProducts(actionSourceNode, cache);
	const productOptions = getProductOptions(products);
	const productType = getProductType(actionSourceNode);
	const store = new CreateSharedDataSheetsWizardStore(ac, cache, onClose, onSave, locale, formData, sourceOptions, productType, productOptions, actionSourceNodeUrl);
	return store;
};

/** Get all the products */
function getProducts(actionSourceNode: C8Node, cache: ICache): C8Node[] {
	if (actionSourceNode.$Type === "SupplierRequest") {
		const lineItems =  cache.getNodes(actionSourceNode.LineItems);
		const products = lineItems.map((lineItem) => cache.get(lineItem.SRLineItemProduct));
		return products;
	}
	return cache.getNodes(actionSourceNode.SRMProducts);
}

/** Make products into ComponentItems to make it available as values for dropdown */
function getProductOptions(products: C8Node[]): ComponentItem[] {
	return convertNodesToComponentItems(products);
}

export function getSourceTable(sourceOption: ComponentItem, fd: FormData, viewParams: Object, canHideTable: boolean): SourceTable {
	return {
		sourceId: sourceOption.id,
		sourceNode: sourceOption.node,
		formData: fd,
		viewParams: viewParams,
		canHideTable: canHideTable,
	};
}

/**
 * Get sources for the field
 * @param actionSourceNode
 * @param cache 
 * @returns supplier or supplierFactory based on subtype
 */
function getSources(actionSourceNode: C8Node, cache: ICache): C8Node[] {
	const nodeType = actionSourceNode.$Type;
	if (nodeType === "SupplierRequest") {
		return Sourcing.isSubtypeSupplierSourced(actionSourceNode.Subtype) ? cache.getNodes(actionSourceNode.SRSuppliers) : cache.getNodes(actionSourceNode.SRSupplierFactories);
	}
	return Sourcing.isSubtypeSupplierSourced(actionSourceNode.SRSubtype) ? cache.getNodes(actionSourceNode.SRMSuppliers) : cache.getNodes(actionSourceNode.SRMSupplierFactories);
}

/**
 * Get source options in SRM based on the product selected
 * @param actionSourceNodeUrl 
 * @param products 
 * @param cache 
 * @param locale 
 * @returns 
 */
export function getSourceOptionsFromProducts(actionSourceNodeUrl: cnl, products: C8Node[], cache: ICache, locale: ILocaleProvider): ComponentItem[] {
	const actionSourceNode = cache.get(actionSourceNodeUrl);
	const srmSourceUrls = getSources(actionSourceNode, cache).map((s) => s.$URL);
	const sources = products.flatMap((p) => {
		const sourcing = cache.get(p.ProductSourcing);
		return cache.getNodes(sourcing.ProductSources).filter((ps) => {
			const source = getPrimarySourceUrl(actionSourceNode, ps);
			return srmSourceUrls.includes(source);
		})
			.map((ps) => cache.get(getPrimarySourceUrl(actionSourceNode, ps)));
	});
	return getSourceOptions(actionSourceNode, [... new Set(sources)], locale);
}

/**
 * Gets Supplier or SupplierFactory of ProductSource based on ProductSourceCreationLevel of SR or SRM
 * @param actionSourceNode 
 * @param ps 
 * @returns Url of Supplier or SupplierFactory of ProductSource
 */
function getPrimarySourceUrl(actionSourceNode: C8Node, ps: C8Node): cnl {
	const nodeType = actionSourceNode.$Type;
	const isSupplierSourced = nodeType === "SupplierRequest" ? Sourcing.isSubtypeSupplierSourced(actionSourceNode.Subtype) : Sourcing.isSubtypeSupplierSourced(actionSourceNode.SRSubtype);
	return isSupplierSourced ? ps.Supplier : ps.SupplierFactory;
}

/**
 * Get Supplier or SupplierFactory string to pass to DataSheet Tables to filter DS
 * @param actionSourceNodeUrl
 * @param cache 
 * @returns 
 */
export function getProductSourceCreationLevelString(actionSourceNodeUrl: cnl, cache: ICache): string {
	const creationLevel = xml.enumGetText(Sourcing.getProductSourceCreationLevel(actionSourceNodeUrl, cache));
	if (creationLevel === "Supplier") {
		return creationLevel;
	}
	return "SupplierFactory";
}
/**
 * @param actionSourceNode
 * @param sources
 * @param locale
 * @returns ComponentItem of the fixed source for dropdown
 */
function getSourceOptions(actionSourceNode: C8Node, sources: C8Node[], locale: ILocaleProvider): ComponentItem[] {
	const nodeType = actionSourceNode.$Type;
	const isSupplierSourced = nodeType === "SupplierRequest" ? Sourcing.isSubtypeSupplierSourced(actionSourceNode.Subtype) : Sourcing.isSubtypeSupplierSourced(actionSourceNode.SRSubtype);
	const supplierOrFactory = isSupplierSourced ? "Supplier" : "SupplierFactory";
	const groupName = locale.type(getProductType(actionSourceNode)) + " " + locale.type(supplierOrFactory, true);
	return convertNodesToComponentItems(sources).map((s) => {
		s.group = groupName;
		return s;
	});
}

/**
 * @param actionSourceNode 
 * @returns product type of SR/SRM derived from its RequestType
 */
function getProductType(actionSourceNode: C8Node): string {
	return xml.enumGetText(actionSourceNode.RequestType);
}

/**
 * Calls the operation to create shared DataSheets on the ProductSource
 * @param actionSourceNodeUrl 
 * @param selectedSourcesUrls 
 * @param dataSheets 
 * @returns 
 */
export async function createSharedDataSheets(sourceToDataSheetsMap: Map<cnl, cnl[]>): Promise<JsonResponse> {
	const mapKeys = Array.from(sourceToDataSheetsMap.keys());
	const resultMap: Record<cnl, string> = {};
	mapKeys.forEach((k) => {
		resultMap[k] = sourceToDataSheetsMap.get(k).toString();
	});
	const writer = new Writer();
	writer.callMethod("Sourcing", "CreateSharedDataSheetsForPS")
		.addParameter("SourceToDataSheetsMap", xml.ATTR_STRINGMAP, resultMap);
	return submitWriter(writer);
}

/**
 * Calls the operation to get a map of Source to LinkedHostDataSheets of the datasheets inside the productSource matching the source
 * @param sources 
 * @param products 
 * @returns 
 */
export async function getSourceLinkedHostDataSheetsMap(sources: cnl[], products: cnl[]): Promise<Object> {
	const request = new PostRequest("Sourcing", "GetSourceLinkedHostDataSheetMap")
		.addParameter("Sources", sources)
		.addParameter("Products", products);

	const response = await request.go();

	return response?.data ?? [];
};

/**
 * Calls the operation to check if the source selected can be hidden/collapsed if there are no shared dataSheets to be created
 * @param sources 
 * @param products 
 * @returns 
 */
export async function getSourcesToHideOnCreateSharedDataSheetsWizard(selectedSources: cnl[], products: cnl[]): Promise<Object> {
	const request = new PostRequest("Specification", "GetSourcesToHideOnCreateSharedDataSheetsWizard")
		.addParameter("Products", products)
		.addParameter("SelectedSources", selectedSources);

	const response = await request.go();

	return response?.data ?? [];
};

